import * as PIXI from "pixi";
import type { ColorMatrix } from "@pixi/filter-color-matrix";

const r = 0.3;
const g = 0.5;
const b = 0.1;
const a = 30;

export default class Cs {
  //}
  static mcw = 622; //600
  static mch = 424; //414

  static DIR = [
    [1, 0],
    [0, 1],
    [-1, 0],
    [0, -1],
  ];

  // GAMEPLAY
  static INTERFACE_MARGIN = 30;

  // COLOR MATRIX
  static CM_GREY: ColorMatrix = [
    r,
    g,
    b,
    0,
    a,
    r,
    g,
    b,
    0,
    a,
    r,
    g,
    b,
    0,
    a,
    0,
    0,
    0,
    1,
    0,
  ];
  static CM_STD: ColorMatrix = [
    1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0,
  ];

  static PIOU_COLOR_MATRIX: ColorMatrix[] = [
    [1, 0, 0, 0, 0, 0, 1, 0, 0, 50, 0, 0, 0.8, 0, 0, 0, 0, 0, 1, 0],
    [0.8, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 70, 0, 0, 0, 1, 0],
    [0.8, 0, 0, 0, 0, 0, 1, 0, 0, 50, 0, 0, 1, 0, 10, 0, 0, 0, 1, 0],
    [1, 0, 0, 0, 50, 0, 1, 0, 0, 10, 0, 0, 0.7, 0, 0, 0, 0, 0, 1, 0],
  ];

  static mm(a: number, b: number, c: number) {
    return Math.min(Math.max(a, b), c);
  }

  static sMod(v: number, mod: number) {
    while (v >= mod) v -= mod;
    while (v < 0) v += mod;
    return v;
  }

  static hMod(v: number, mod: number) {
    while (v > mod) v -= mod * 2;
    while (v < -mod) v += mod * 2;
    return v;
  }

  // GET RY
  static gry(levelHeight: number, y: number) {
    return levelHeight - y;
  }

  // COLOR
  static colToObj(col: number) {
    return {
      r: col >> 16,
      g: (col >> 8) & 0xff,
      b: col & 0xff,
    };
  }

  static objToCol(o: { a?: number; r: any; g: any; b: any }) {
    return (o.r << 16) | (o.g << 8) | o.b;
  }

  static colToObj32(col: number) {
    return {
      a: col >>> 24,
      r: (col >> 16) & 0xff,
      g: (col >> 8) & 0xff,
      b: col & 0xff,
    };
  }

  static objToCol32(o: { r: any; g: any; b: any; a: any }) {
    return (o.a << 24) | (o.r << 16) | (o.g << 8) | o.b;
  }

  static setPercentColor(
    mc: PIXI.animate.DisplayObject,
    prc: number,
    col: number
  ) {
    var color = Cs.colToObj(col);
    var c = prc / 100;
    var ct = {
      ra: Math.floor(100 - prc),
      ga: Math.floor(100 - prc),
      ba: Math.floor(100 - prc),
      aa: 100,
      rb: Math.floor(c * color.r),
      gb: Math.floor(c * color.g),
      bb: Math.floor(c * color.b),
      ab: 0,
    };
    // TODO: check if it's correct !
    mc.setColorTransform(ct.ra, ct.rb, ct.ga, ct.gb, ct.ba, ct.bb);
  }

  static setColor(
    mc: PIXI.animate.DisplayObject,
    col: number,
    dec: number | null
  ) {
    if (dec == null) dec = -255;
    var o = Cs.colToObj32(col);
    var ct = {
      ra: 100,
      ga: 100,
      ba: 100,
      aa: 100,
      rb: o.r + dec,
      gb: o.g + dec,
      bb: o.b + dec,
      ab: 0,
    };
    // TODO: check if it's correct !
    mc.setColorTransform(ct.ra, ct.rb, ct.ga, ct.gb, ct.ba, ct.bb);
  }

  static setColorMatrix(
    mc: PIXI.animate.DisplayObject,
    m: ColorMatrix,
    dec: number | null
  ) {
    if (dec != null) {
      m = [...m] as ColorMatrix;
      for (let i = 0; i < 3; i++) {
        m[4 + 5 * i] = dec;
      }
    }

    mc.colorTransformFilter = new PIXI.filters.ColorMatrixFilter(m);
  }

  static col32to16(c: any) {
    var o = Cs.colToObj32(c);
    return Cs.objToCol(o);
  }

  static getAng(o: { x: number; y: number }, o2: { x: number; y: number }) {
    var dx = o.x - o2.x;
    var dy = o.y - o2.y;
    return Math.atan2(dy, dx);
  }

  static getDist(o: { x: number; y: number }, o2: { x: number; y: number }) {
    var dx = o.x - o2.x;
    var dy = o.y - o2.y;
    return Math.sqrt(dx * dx + dy * dy);
  }
}
